import { applyMiddleware, compose, createStore } from 'redux'
import { createEpicMiddleware } from 'redux-observable'
import thunk from 'redux-thunk'
import { Subject } from 'rxjs'
import { mergeMap } from 'rxjs/operators'

import { sanitizePSAWizardAction } from './containers/Settings/MalwareDetection/PSA/devToolsSanitizer'
import { createEpicRegistry } from './utils/epicRegistry'
import { featureFlagsIdentityMiddleware } from './utils/featureFlags/middleware'
import ls from './utils/LocalStorage'
import { pendoMSPSupportMiddleware } from './utils/pendo'
import { attachReducerRegistry } from './utils/reducerRegistry'
import { requestErrorHandler } from './utils/requestErrorHandler'
import { usageOutcomeMiddleware } from './utils/usageAnalytics'
import api from './api'
import { ALWAYS_ON_EPICS, epicsErrorHandler } from './epics'
import createReducer from './reducer'

export default () => {
  ls.init()

  // The epic stream: a Subject of per-key `lifetime` functions, flattened
  // via mergeMap. `runEpic` is the registry's handle onto it.
  const epic$ = new Subject()
  const runEpic = (epic) => epic$.next(epic)
  const registry = createEpicRegistry({ runEpic })

  // The root epic flattens the per-key `lifetime` streams the registry hands
  // it. redux-observable 1.x passes `state$` (a StateObservable whose `.value`
  // is the current state) as the second argument; every epic reads it that way.
  const rootEpic = (action$, state$, deps) =>
    epic$.pipe(mergeMap((epic) => epic(action$, state$, deps)))

  const epicMiddleware = createEpicMiddleware({
    dependencies: {
      ls,
      api,
      epicsErrorHandler,
    },
  })

  const middleware = [
    epicMiddleware,
    thunk.withExtraArgument({ api }),
    requestErrorHandler,
    pendoMSPSupportMiddleware,
    usageOutcomeMiddleware,
    featureFlagsIdentityMiddleware,
  ]

  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ // eslint-disable-line
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        // eslint-disable-line
        shouldHotReload: false,
        trace: true,
        actionsDenylist: ['INCIDENTS_COUNT_POLLING_ASYNC'],
        actionSanitizer: sanitizePSAWizardAction,
      })
    : compose

  const enhancer = composeEnhancers(applyMiddleware(...middleware))

  const store = attachReducerRegistry(createStore(createReducer(), enhancer))

  // `run` must follow createStore (the middleware needs the store) and must
  // precede the always-on injection below, so the root stream exists before
  // the first `lifetime` is pushed into epic$.
  epicMiddleware.run(rootEpic)
  store.injectEpic = registry.injectEpic
  store.ejectEpic = registry.ejectEpic
  store.getRunningEpicKeys = registry.getRunningEpicKeys

  // Always-on epics are injected synchronously, immediately after
  // createStore and before index.js calls initFeatureFlags()/root.render():
  // action$ is a plain Subject and does not replay, so any action dispatched
  // before injection would otherwise be lost.
  ALWAYS_ON_EPICS.forEach(([key, epic]) => store.injectEpic(key, epic))

  return store
}
